import React, { useState, useEffect } from 'react';
import { tenantsAPI } from '../../services/api';
import PaginatedTable from '../../components/PaginatedTable';
import { useI18n } from '../../i18n/I18nContext';

const TenantsManager = () => {
    const { t } = useI18n();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [formData, setFormData] = useState({ 
        name: '', 
        subdomain: '',
        latitude: '',
        longitude: '',
        google_maps_link: ''
    });

    useEffect(() => {
        fetchTenants();
    }, []);

    const extractCoords = (link) => {
        if (!link) return;
        // Match @lat,lng
        const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
        const match = link.match(regex);
        if (match) {
            setFormData(prev => ({ ...prev, latitude: match[1], longitude: match[2] }));
        }
    };

    const fetchTenants = async () => {
        setLoading(true);
        try {
            const res = await tenantsAPI.superList();
            setTenants(res.data);
        } catch (err) {
            console.error('Error fetching tenants:', err);
        }
        setLoading(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                latitude: formData.latitude ? parseFloat(formData.latitude) : null,
                longitude: formData.longitude ? parseFloat(formData.longitude) : null
            };
            if (editingTenant) {
                await tenantsAPI.superUpdate(editingTenant.id, payload);
            } else {
                await tenantsAPI.superCreate(payload);
            }
            setShowModal(false);
            setEditingTenant(null);
            setFormData({ name: '', subdomain: '', latitude: '', longitude: '', google_maps_link: '' });
            fetchTenants();
        } catch (err) {
            alert(err.response?.data?.error || 'Error saving tenant');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Are you sure?'))) return;
        try {
            await tenantsAPI.superRemove(id);
            fetchTenants();
        } catch (err) {
            alert('Error deleting tenant');
        }
    };

    const columns = [
        { label: t('Name'), key: 'name' },
        { label: t('Subdomain'), key: 'subdomain' },
        { 
            label: t('Location'), 
            key: 'location',
            render: (_, row) => row.latitude ? `${row.latitude}, ${row.longitude}` : t('Not set')
        },
        { label: t('Created At'), key: 'created_at', render: (val) => new Date(val).toLocaleString() },
        {
            label: t('Actions'),
            key: 'actions',
            render: (_, row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => {
                            setEditingTenant(row);
                            setFormData({ 
                                name: row.name, 
                                subdomain: row.subdomain,
                                latitude: row.latitude || '',
                                longitude: row.longitude || '',
                                google_maps_link: row.google_maps_link || ''
                            });
                            setShowModal(true);
                        }}
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
                    >
                        {t('Edit')}
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                    >
                        {t('Delete')}
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('Tenants Management')}</h1>
                        <p className="text-gray-500 mt-1">{t('Manage school districts and institutional isolation')}</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingTenant(null);
                            setFormData({ name: '', subdomain: '', latitude: '', longitude: '', google_maps_link: '' });
                            setShowModal(true);
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-md transition-all active:scale-95 flex items-center gap-2"
                    >
                        <span className="text-xl">+</span> {t('Add Tenant')}
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <PaginatedTable
                        columns={columns}
                        data={tenants}
                        loading={loading}
                    />
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
                        <div className="bg-indigo-600 px-6 py-4">
                            <h2 className="text-xl font-bold text-white">
                                {editingTenant ? t('Edit Tenant') : t('Add Tenant')}
                            </h2>
                        </div>
                        <form onSubmit={handleSave} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Name')}</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-outline"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="e.g. Oakridge Academy"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Subdomain')}</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-outline"
                                        value={formData.subdomain}
                                        onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                                        required
                                        placeholder="e.g. oakridge"
                                    />
                                </div>
                                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                                    <label className="block text-sm font-bold text-indigo-900 mb-2">{t('Geolocalisation')}</label>
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            className="w-full border border-indigo-200 rounded-lg p-2 text-sm"
                                            value={formData.google_maps_link}
                                            onChange={(e) => {
                                                setFormData({ ...formData, google_maps_link: e.target.value });
                                                extractCoords(e.target.value);
                                            }}
                                            placeholder={t('Google Maps Link')}
                                        />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="number"
                                                step="any"
                                                className="w-full border border-indigo-200 rounded-lg p-2 text-sm"
                                                value={formData.latitude}
                                                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                                                placeholder="Lat"
                                            />
                                            <input
                                                type="number"
                                                step="any"
                                                className="w-full border border-indigo-200 rounded-lg p-2 text-sm"
                                                value={formData.longitude}
                                                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                                                placeholder="Lng"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition"
                                >
                                    {t('Save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantsManager;
